// Detects a real scroll gesture by the visitor.
//
// Infinite scroll (paginated-list.js) loads the next page as soon as its sentinel enters the
// viewport, then pushes `?page=2` into the URL. Headless renderers open pages with a very tall
// viewport and never scroll: the sentinel is visible at once and the URL changes, which reads
// as a redirect. Automatic loading is therefore armed only after a real gesture: scroll,
// wheel, touch or a navigation key. A page opened already scrolled (browser restore, back
// navigation) counts as a gesture.
//
// Pure module (no global DOM).

export const INTENT_KEYS = ['PageDown', 'PageUp', 'ArrowDown', 'ArrowUp', 'End', 'Home', ' ', 'Spacebar'];

const INTENT_EVENTS = ['scroll', 'wheel', 'touchmove', 'keydown'];

/**
 * @param {{ scrollY?: number, addEventListener: Function, removeEventListener: Function }} target - window in practice
 * @returns {{ readonly armed: boolean, onArmed: (cb: () => void) => void, dispose: () => void }}
 */
export function createScrollIntent(target) {
  let armed = false;
  /** @type {Array<() => void>} */
  let callbacks = [];

  const remove = () => {
    for (const type of INTENT_EVENTS) target.removeEventListener(type, onEvent);
  };

  const arm = () => {
    if (armed) return;
    armed = true;
    remove();
    const cbs = callbacks;
    callbacks = [];
    for (const cb of cbs) cb();
  };

  /** @param {{ key?: string, type?: string }} [ev] */
  function onEvent(ev) {
    if (ev && ev.key !== undefined && !INTENT_KEYS.includes(ev.key)) return; // keydown outside navigation keys
    arm();
  }

  if ((target.scrollY || 0) > 0) {
    armed = true;
  } else {
    for (const type of INTENT_EVENTS) target.addEventListener(type, onEvent, { passive: true });
  }

  return {
    get armed() { return armed; },
    onArmed(cb) { if (armed) cb(); else callbacks.push(cb); },
    dispose() { remove(); callbacks = []; },
  };
}
